import type { Middleware } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { createTransform, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { createLogger } from 'redux-logger';
import rootReducer from './reducers';
import { logout, refreshToken, shouldRefreshToken } from './slices/auth';
import type { AnyAction, MiddlewareAPI } from 'redux';

const excludeTutorialState = createTransform(
    (inboundState, key) => {
        if (typeof key === 'string' && ['help', 'searchBar', 'menu'].includes(key)) {
            return undefined;
        }
        return inboundState;
    },
    undefined
);

const persistWhitelist: (keyof ReturnType<typeof rootReducer>)[] = [
    'newSpot',
    'user',
    'auth',
    'spots',
    'file',
    'sessionStatus',
    'home',
    'location',
    'searchBar',
    'menu',
    'map',
    'help',
];

const persistConfig = {
    key: 'state',
    storage,
    whitelist: persistWhitelist as string[],
    transforms: [excludeTutorialState],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export type RootState = ReturnType<typeof rootReducer>;

const middlewares: Middleware[] = [];
if (process.env.NODE_ENV !== 'production') {
    middlewares.push(createLogger());
}

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
        }).concat(middlewares),
    devTools: true,
});

export type AppDispatch = typeof store.dispatch;

const customMiddleware: Middleware<unknown, RootState, AppDispatch> =
    (storeAPI: MiddlewareAPI<AppDispatch, RootState>) =>
        (next) =>
            (action: AnyAction) => {
                const state = storeAPI.getState();
                const auth = state.auth;
                const tokenExp = auth.tokenData?.exp;

                if (
                    !action.type.includes(refreshToken.typePrefix) &&
                    !action.type.includes(logout.type) &&
                    !auth.refreshingToken
                ) {
                    if (shouldRefreshToken(tokenExp)) {
                        void storeAPI.dispatch(refreshToken());
                    }
                }

                return next(action);
            };

middlewares.push(customMiddleware);

export const persistor = persistStore(store);
